import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from '../config.service';
import { PageTypes } from '../../enums/common.enum';
import { InitializeJobPagePayload } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class JobPageApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService
  ) {}

  private jobPageUrl(path: string, isPublic = false): string {
    return this.configService.getBffUrl('jpage', path, { public: isPublic });
  }

  updateTemplateBychat<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('ai/chat'), payload);
  }

  getAllLandingPages<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('pages/list'), payload);
  }

  getPageDetailsById<T>(uniqueCode: string): Observable<T> {
    return this.http.get<T>(
      this.jobPageUrl(`pages/${uniqueCode}?status=draft`)
    );
  }

  deleteLandingPage<T>(id: number): Observable<T> {
    return this.http.delete<T>(this.jobPageUrl(`pages/${id}`));
  }

  updateLandingPageData<T>(
    payload: unknown,
    actionType?: string
  ): Observable<T> {
    const path =
      actionType === PageTypes.DUPLICATE ||
      actionType === PageTypes.USE_AS_TEMPLATE
        ? 'pages/duplicate-with-payload'
        : 'pages/upsert';

    return this.http.post<T>(this.jobPageUrl(path), payload);
  }

  promptConfiguration<T>(
    method: 'GET' | 'PUT',
    adminPassword: string,
    payload?: { id?: number; [key: string]: unknown }
  ): Observable<T> {
    const suffix = method === 'PUT' && payload?.id ? `/${payload.id}` : '';
    const url = this.jobPageUrl(`ai/prompt${suffix}`);
    const options = {
      headers: { 'X-Prompt-Admin-Password': adminPassword },
    };

    if (method === 'GET') {
      return this.http.get<T>(url, options);
    }

    return this.http.put<T>(url, payload, options);
  }

  getTemplatesByClient<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(
      this.jobPageUrl('template/template/by-client'),
      payload
    );
  }

  getTemplateDetailsById<T>(uniqueCode: string): Observable<T> {
    return this.http.get<T>(
      this.jobPageUrl(`template/${encodeURIComponent(uniqueCode)}`)
    );
  }

  upsertPagePublish<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('page/toggle-status'), payload);
  }

  getPublicPageDetailsById<T>(uniqueCode: string): Observable<T> {
    const query = new URLSearchParams({ status: 'published' });
    return this.http.get<T>(
      `${this.jobPageUrl(`pages/${encodeURIComponent(uniqueCode)}`, true)}?${query.toString()}`
    );
  }

  checkPageNameKey<T>(params: {
    page_id: number;
    short_key: string;
    domain_name: string;
  }): Observable<T> {
    const query = new URLSearchParams({
      page_id: String(params.page_id),
      short_key: params.short_key,
      domain_name: params.domain_name,
    });
    return this.http.get<T>(
      this.jobPageUrl(`url-settings/check-key?${query.toString()}`)
    );
  }

  checkFolioUrlSettingKey<T>(params: {
    page_id?: number;
    short_key?: string;
    domain_name?: string;
  }): Observable<T> {
    const query = new URLSearchParams();
    if (params.page_id != null) query.set('page_id', String(params.page_id));
    query.set('short_key', params.short_key ?? '');
    if (params.domain_name) query.set('domain_name', params.domain_name);

    return this.http.get<T>(
      this.jobPageUrl(
        `url-settings/CheckFolioUrlSettingKey?${query.toString()}`
      )
    );
  }

  upsertCustomFont<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('files'), payload);
  }

  getCustomFontList<T>(payload: {
    client_code: string;
    pageIndex: number;
    pageSize: number;
  }): Observable<T> {
    return this.http.get<T>(
      this.jobPageUrl(
        `files/client/${payload.client_code}?page=${payload.pageIndex}&page_size=${payload.pageSize}`
      )
    );
  }

  deleteCustomFont<T>(fontId: string | number): Observable<T> {
    return this.http.delete<T>(this.jobPageUrl(`files/${fontId}`));
  }

  initializePageBuilder<T>(payload: InitializeJobPagePayload): Observable<T> {
    return this.http.post<T>(
      this.jobPageUrl('job-page-agent/initialize'),
      payload
    );
  }

  getAgentChatHistory<T>(sessionId: string): Observable<T> {
    return this.http.get<T>(
      this.jobPageUrl(`job-page-agent/chat-messages?session_id=${sessionId}`)
    );
  }

  startChatWithAgent<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('job-page-agent/chat'), payload);
  }

  createNewTemplate<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('template/upsert'), payload);
  }

  generatePrerenderedPageUrl<T>(pageUrl: string): Observable<T> {
    const query = new URLSearchParams({ url: pageUrl });
    const url = `${this.configService.getBffUrl('node', 'prerender/recache')}?${query.toString()}`;
    return this.http.get<T>(url);
  }

  uploadMediaOnCloudinary<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(
      this.jobPageUrl('cloudinary/upload-from-url-v2'),
      payload
    );
  }

  unpublishPage<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('pages/unpublish'), payload);
  }

  changePageVacancy<T>(payload: {
    id: number;
    client_code: string;
    title: string;
    source_id: string;
    source_title?: string;
  }): Observable<T> {
    return this.http.post<T>(
      this.jobPageUrl('pages/update-page-info'),
      payload
    );
  }

  deleteTemplate<T>(templateId: number): Observable<T> {
    return this.http.delete<T>(this.jobPageUrl(`template/${templateId}`));
  }

  updateTemplate<T>(payload: unknown): Observable<T> {
    return this.http.post<T>(this.jobPageUrl('template/update'), payload);
  }

  resolveVacancyPrompt<T>(
    templateId: number | string,
    payload: { vacancyId: string }
  ): Observable<T> {
    return this.http.post<T>(
      this.jobPageUrl(`template/${templateId}/resolve-vacancy-prompt`),
      payload
    );
  }
}
